import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
  MdOutlineAdd,
  MdOutlineRemove,
  MdOutlineSwapHoriz,
} from "react-icons/md";
import axios from "../../config/axiosInstance";
import { EVENT_SIGNUPS } from "../../config/apiConfig";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import Modal from "../modals/Modal";
import { ErrorBlock, SkeletonRows } from "../crm/Primitives";
import { formatMoney } from "../crm/crmHelpers";

/*
  Escolher ou mudar o plano que a equipe vai cobrar.

  Só grava o pedido (o servidor recalcula o valor): não cobra nada, não troca o
  plano da conta e não registra pagamento. Quem libera o plano é "Cobrar plano".

  A tabela de valores vem do servidor (`plan-options`), com a mesma conta que o
  pedido grava, então o que aparece aqui é exatamente o que será cobrado. Nada
  de preço escrito nesta tela.

  Abre no plano e ciclo do pedido atual. Para quem ainda não escolheu nada,
  começa no anual, que é o que a vitrine do cadastro do evento destaca.

  PROFISSIONAIS: o valor é por profissional (mensalidade × profissionais × meses),
  e a equipe do balcão costuma ser maior que a do cadastro. O campo "Profissionais
  no plano" muda a quantidade e os valores são pedidos de novo ao servidor com ela
  (a tela continua sem conta própria). A quantidade vale para a cobrança e, quando
  o pagamento entra, vira a equipe da conta. No Diamond o valor não depende dela.
*/

const MAX_USERS = 200;

const DEFAULT_PLAN = "PRO";
const DEFAULT_CYCLE = "ANNUAL";

const labelClass =
  "mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400";

const ChangePlanModal = ({ signup, thenCharge = false, onClose, onSaved }) => {
  const { company, request } = signup;

  // O pedido pendente guarda a quantidade já ajustada, menos no Diamond (guarda
  // 1, porque o valor não depende dela): ali vale a equipe do cadastro.
  const initialUsers =
    request?.status === "PENDING" && request.planId !== "DIAMOND"
      ? request.numberOfUsers
      : signup.answers?.teamSize || 1;

  const [usersText, setUsersText] = useState(String(initialUsers));
  const [planId, setPlanId] = useState(request?.planId || DEFAULT_PLAN);
  const [cycleId, setCycleId] = useState(request?.billingCycle || DEFAULT_CYCLE);
  const [error, setError] = useState(null);

  const parsedUsers = Number(usersText);
  const users =
    usersText.trim() !== "" &&
    Number.isInteger(parsedUsers) &&
    parsedUsers >= 1 &&
    parsedUsers <= MAX_USERS
      ? parsedUsers
      : null;
  // Cada clique no + / − mudaria o valor; espera a pessoa parar antes de pedir.
  const debouncedUsers = useDebouncedValue(users, 300);

  const options = useQuery(
    ["events", "plan-options", company.id, debouncedUsers],
    async () => {
      const { data } = await axios.get(
        `${EVENT_SIGNUPS.PLAN_OPTIONS}/${company.id}/plan-options?users=${debouncedUsers}`
      );
      return data.data;
    },
    {
      enabled: debouncedUsers !== null,
      // O valor depende da equipe, que pode mudar entre uma abertura e outra:
      // nunca reaproveitar. Ao mudar a quantidade, a tela mantém os valores
      // antigos até chegarem os novos (nada de piscar o esqueleto).
      cacheTime: 0,
      refetchInterval: false,
      keepPreviousData: true,
    }
  );

  // Os valores na tela ainda são de outra quantidade: não deixa salvar até chegarem.
  const pricing = users === null || users !== debouncedUsers || options.isFetching;

  const save = useMutation(
    () =>
      axios.put(`${EVENT_SIGNUPS.PLAN}/${company.id}/plan`, {
        planId,
        billingCycle: cycleId,
        numberOfUsers: users,
      }),
    {
      onSuccess: ({ data }) => onSaved?.(data.data),
      onError: (err) =>
        setError(
          err.response?.data?.message || "Não foi possível salvar o plano."
        ),
    }
  );

  const plans = options.data?.plans || [];
  const selectedPlan = plans.find((plan) => plan.id === planId) || plans[0];
  const cycles = selectedPlan?.cycles || [];
  const selected = cycles.find((cycle) => cycle.id === cycleId) || cycles[0];

  const unchanged =
    request?.status === "PENDING" &&
    request.planId === selected?.planId &&
    request.billingCycle === selected?.id &&
    request.numberOfUsers === selected?.numberOfUsers;

  const saveLabel = thenCharge ? "Salvar e cobrar" : "Salvar plano";

  return (
    <Modal
      isOpen
      onClose={() => !save.isLoading && onClose()}
      size="md"
      icon={MdOutlineSwapHoriz}
      title={request ? "Mudar plano" : "Escolher plano"}
      subtitle={company.name}
      closeOnBackdrop={!save.isLoading}
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={save.isLoading}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              // Nada mudou: segue direto para a cobrança, sem gravar de novo.
              if (unchanged && thenCharge) onSaved?.(request);
              else save.mutate();
            }}
            disabled={
              !selected ||
              pricing ||
              save.isLoading ||
              (unchanged && !thenCharge)
            }
            className="rounded-xl bg-pink-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-pink-700 disabled:opacity-40"
          >
            {save.isLoading ? "Salvando..." : saveLabel}
          </button>
        </div>
      }
    >
      <fieldset className="mb-4">
        <legend className={labelClass}>Profissionais no plano</legend>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Menos um profissional"
            disabled={save.isLoading || (users ?? 1) <= 1}
            onClick={() => setUsersText(String(Math.max((users ?? 2) - 1, 1)))}
            className="rounded-xl border border-slate-200 p-2.5 text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
          >
            <MdOutlineRemove size={18} />
          </button>
          <input
            id="plan-users"
            inputMode="numeric"
            aria-label="Quantidade de profissionais"
            value={usersText}
            disabled={save.isLoading}
            onChange={(event) => {
              setUsersText(event.target.value.replace(/\D/g, "").slice(0, 3));
              setError(null);
            }}
            className="w-20 rounded-xl border border-slate-200 px-3 py-2 text-center text-base font-bold text-slate-900 outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
          />
          <button
            type="button"
            aria-label="Mais um profissional"
            disabled={save.isLoading || (users ?? 0) >= MAX_USERS}
            onClick={() => setUsersText(String(Math.min((users ?? 0) + 1, MAX_USERS)))}
            className="rounded-xl border border-slate-200 p-2.5 text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
          >
            <MdOutlineAdd size={18} />
          </button>
        </div>
        {users === null ? (
          <p role="alert" className="mt-1 text-[11px] font-semibold text-rose-700">
            Informe de 1 a {MAX_USERS} profissionais.
          </p>
        ) : selectedPlan?.id === "DIAMOND" ? (
          <p className="mt-1 text-[11px] text-slate-500">
            No Diamond o valor é o mesmo para qualquer quantidade.
          </p>
        ) : (
          <p className="mt-1 text-[11px] text-slate-500">
            {signup.answers?.teamSize && signup.answers.teamSize !== users
              ? `A cliente se cadastrou com ${signup.answers.teamSize}. `
              : ""}
            Quando o pagamento entrar, essa passa a ser a equipe da conta.
          </p>
        )}
      </fieldset>

      {options.isLoading ? (
        <SkeletonRows rows={4} />
      ) : options.error ? (
        <ErrorBlock
          message={
            options.error.response?.data?.message ||
            "Não foi possível carregar os planos."
          }
          onRetry={options.refetch}
        />
      ) : (
        <div className="space-y-4">
          {error ? (
            <p
              role="alert"
              className="rounded-xl bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-800 ring-1 ring-inset ring-rose-200"
            >
              {error}
            </p>
          ) : null}

          <fieldset>
            <legend className={labelClass}>Plano</legend>
            <div className="grid grid-cols-3 gap-2" role="radiogroup">
              {plans.map((plan) => {
                const active = plan.id === selectedPlan?.id;
                return (
                  <button
                    key={plan.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => {
                      setPlanId(plan.id);
                      setError(null);
                    }}
                    className={`rounded-xl border px-3 py-3 text-center text-sm font-bold transition ${
                      active
                        ? "border-pink-500 bg-pink-50 text-pink-700 ring-2 ring-pink-100"
                        : "border-slate-200 bg-white text-slate-700 hover:border-pink-200"
                    }`}
                  >
                    {plan.name}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <fieldset>
            <legend className={labelClass}>Ciclo</legend>
            <div className="grid grid-cols-2 gap-2" role="radiogroup">
              {cycles.map((cycle) => {
                const active = cycle.id === selected?.id;
                return (
                  <button
                    key={cycle.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => {
                      setCycleId(cycle.id);
                      setError(null);
                    }}
                    className={`rounded-xl border px-3 py-2.5 text-left transition ${
                      active
                        ? "border-pink-500 bg-pink-50 ring-2 ring-pink-100"
                        : "border-slate-200 bg-white hover:border-pink-200"
                    }`}
                  >
                    <span className="block text-sm font-bold text-slate-900">
                      {cycle.label}
                    </span>
                    <span className="block text-[11px] text-slate-500">
                      {formatMoney(cycle.amount)}
                    </span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          {selected ? (
            <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-inset ring-slate-200">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Total a cobrar
              </p>
              <p className="mt-1 text-3xl font-bold leading-none text-slate-900">
                {formatMoney(selected.amount)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {selectedPlan.id === "DIAMOND"
                  ? "Equipe inteira inclusa"
                  : `${selected.numberOfUsers} profissional(is) × ${formatMoney(
                      selected.monthlyPrice
                    )} × ${selected.months} mês(es)`}
              </p>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
};

export default ChangePlanModal;
